import { add, sub, isWithinInterval, parseISO, format, startOfDay } from "date-fns";
import {
  collection,
  query,
  getDocs,
  where,
  orderBy,
  limit,
  Timestamp,
  DocumentData,
} from "firebase/firestore";
import { db } from "./firebase";

// Type definitions for Google Ads spend data
export interface AdsSpendEntry {
  id: string;
  businessId: string;
  date: string;
  amount: number;
  campaign: string;
}

// Type definition for Firestore AdSpend document
interface FirestoreAdSpendDoc {
  date: string;
  metrics: {
    average_cpc?: number;
    average_cpm?: number;
    clicks?: number;
    conversion_value?: number;
    conversions?: number;
    cost: number;
    ctr?: number;
    currency_code?: string;
    impressions?: number;
    syncTime?: Timestamp;
  };
  override?: {
    cost: number;
  };
}

// Function to generate dummy Google Ads spend data for a business
export const generateAdsSpendData = (
  businessId: string,
  entryCount: number = 100,
): AdsSpendEntry[] => {
  const result: AdsSpendEntry[] = [];
  const now = new Date();
  const campaigns = ["Search", "Display", "Video", "Shopping"];

  // Determine how many entries to generate for each time period
  const last30DaysCount = Math.floor(entryCount * 0.4); // 40% in last 30 days
  const last60DaysCount = Math.floor(entryCount * 0.2); // 20% in days 31-60
  const last90DaysCount = Math.floor(entryCount * 0.15); // 15% in days 61-90
  const remainingCount =
    entryCount - last30DaysCount - last60DaysCount - last90DaysCount; // Rest spread through the year

  // Generate entries for the last 30 days (40% of entries)
  for (let i = 0; i < last30DaysCount; i++) {
    const daysAgo = Math.floor(Math.random() * 30);
    const entryDate = sub(now, { days: daysAgo });

    // Random amount between $10 and $75 for recent spend (higher budget)
    const amount = Math.round((10 + Math.random() * 65) * 100) / 100;

    // Random campaign
    const campaign = campaigns[Math.floor(Math.random() * campaigns.length)];

    result.push({
      id: `ads-${businessId}-last30-${i}`,
      businessId,
      date: entryDate.toISOString(),
      amount,
      campaign,
    });
  }

  // Generate entries for days 31-60 (20% of entries)
  for (let i = 0; i < last60DaysCount; i++) {
    const daysAgo = 30 + Math.floor(Math.random() * 30);
    const entryDate = sub(now, { days: daysAgo });

    // Random amount between $8 and $60 for mid-term spend
    const amount = Math.round((8 + Math.random() * 52) * 100) / 100;

    // Random campaign
    const campaign = campaigns[Math.floor(Math.random() * campaigns.length)];

    result.push({
      id: `ads-${businessId}-last60-${i}`,
      businessId,
      date: entryDate.toISOString(),
      amount,
      campaign,
    });
  }

  // Generate entries for days 61-90 (15% of entries)
  for (let i = 0; i < last90DaysCount; i++) {
    const daysAgo = 60 + Math.floor(Math.random() * 30);
    const entryDate = sub(now, { days: daysAgo });

    // Random amount between $5 and $50 for older spend
    const amount = Math.round((5 + Math.random() * 45) * 100) / 100;

    // Random campaign
    const campaign = campaigns[Math.floor(Math.random() * campaigns.length)];

    result.push({
      id: `ads-${businessId}-last90-${i}`,
      businessId,
      date: entryDate.toISOString(),
      amount,
      campaign,
    });
  }

  // Generate remaining entries for the rest of the year (spread out)
  for (let i = 0; i < remainingCount; i++) {
    // Random date within past year (but beyond 90 days)
    const daysAgo = 90 + Math.floor(Math.random() * (365 - 90));
    const entryDate = sub(now, { days: daysAgo });

    // Random amount between $3 and $40 for oldest spend (lower budget historically)
    const amount = Math.round((3 + Math.random() * 37) * 100) / 100;

    // Random campaign
    const campaign = campaigns[Math.floor(Math.random() * campaigns.length)];

    result.push({
      id: `ads-${businessId}-older-${i}`,
      businessId,
      date: entryDate.toISOString(),
      amount,
      campaign,
    });
  }

  return result;
};

// Function to fetch AdSpend data from Firestore
export const fetchAdsSpendDataFromFirestore = async (
  businessId: string,
  timeFrame: string = "Past 30 days",
): Promise<AdsSpendEntry[]> => {
  try {
    // Determine date range based on timeFrame
    const now = new Date();
    let startDate: Date;

    switch (timeFrame) {
      case "Past 7 days":
        startDate = sub(now, { days: 7 });
        break;
      case "Past 30 days":
        startDate = sub(now, { days: 30 });
        break;
      case "Past 90 days":
        startDate = sub(now, { days: 90 });
        break;
      case "Past year":
        startDate = sub(now, { years: 1 });
        break;
      case "All time":
      default:
        // For "All time", use a date far in the past
        startDate = sub(now, { years: 10 });
        break;
    }

    // Format the start date as YYYY-MM-DD for Firestore query
    const startDateStr = format(startDate, "yyyy-MM-dd");

    // Create a reference to the AdSpend subcollection
    const adSpendCollectionRef = collection(
      db,
      "businesses",
      businessId,
      "adspend",
    );

    // Create a query to get documents within the date range
    // We're querying by document ID since the ID is the date in YYYY-MM-DD format
    const q = query(
      adSpendCollectionRef,
      where("date", ">=", startDateStr),
      orderBy("date", "desc"),
    );

    // Execute the query
    const querySnapshot = await getDocs(q);

    // Map the documents to AdsSpendEntry objects
    const entries: AdsSpendEntry[] = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data() as FirestoreAdSpendDoc;

      if (
        data &&
        data.metrics &&
        (data.override?.cost !== undefined || data.metrics.cost !== undefined)
      ) {
        let cost = data.override?.cost;
        if (cost === undefined) cost = data.metrics.cost;
        if (cost === undefined) cost = 0;

        entries.push({
          id: doc.id,
          businessId,
          date: data.date,
          amount: cost,
          campaign: "All Campaigns", // Default campaign name since we don't have campaign-level data
        });
      }
    });

    console.log(
      `Fetched ${entries.length} AdSpend entries from Firestore for business ${businessId}`,
    );
    return entries;
  } catch (error) {
    console.error("Error fetching AdSpend data from Firestore:", error);
    // Return empty array in case of error
    return [];
  }
};

// Get cached ads spend data for a business
const adsSpendCache: Record<string, AdsSpendEntry[]> = {};
export const getAdsSpendData = async (
  businessId: string,
): Promise<AdsSpendEntry[]> => {
  if (!adsSpendCache[businessId]) {
    try {
      // Try to fetch data from Firestore first
      const firestoreData = await fetchAdsSpendDataFromFirestore(businessId);

      if (firestoreData.length > 0) {
        // If we got data from Firestore, use it
        adsSpendCache[businessId] = firestoreData;
      }
    } catch (error) {
      // If there's an error fetching from Firestore, fall back to generated data
      console.error(
        "Error fetching AdSpend data, falling back to generated data:",
        error,
      );
    }
  }
  return adsSpendCache[businessId];
};

// Filter ads spend data by timeframe
export const filterAdsByTimeframe = (
  adsSpendData: AdsSpendEntry[],
  timeFrame: string,
): AdsSpendEntry[] => {
  let startDate: Date;

  const now = startOfDay(new Date(Date.UTC(
    new Date().getUTCFullYear(),
    new Date().getUTCMonth(),
    new Date().getUTCDate()
  )));

  switch (timeFrame) {
    case "Past 7 days":
      startDate = sub(now, { days: 7 });
      break;
    case "Past 30 days":
      startDate = sub(now, { days: 30 });
      break;
    case "Past 90 days":
      startDate = sub(now, { days: 90 });
      break;
    case "Past year":
      startDate = sub(now, { years: 1 });
      break;
    case "All time":
    default:
      // Return all data for "All time"
      return adsSpendData;
  }

  return adsSpendData.filter((entry) => {
    const entryDate = parseISO(entry.date);
    return entryDate >= startDate && entryDate < now;
  });
};

// Calculate total ad spend for a given timeframe
export const calculateTotalAdSpend = async (
  businessId: string,
  timeFrame: string,
): Promise<number> => {
  const adsData = await getAdsSpendData(businessId);
  const filteredAds = filterAdsByTimeframe(adsData, timeFrame);

  return filteredAds.reduce((total, entry) => total + entry.amount, 0);
};
